import { useCallback, useEffect, useRef, useState } from 'react'
import { login as doLogin, logout as doLogout, getStoredAuth, type AuthState } from './login'
import { startSso } from './sso'
import { resolveAuthToken } from './resolveToken'

export interface UseAuth {
  auth: AuthState | null
  status: 'checking' | 'authenticated' | 'unauthenticated'
  error: string | null
  signingIn: boolean
  signIn: (email: string, password: string) => Promise<void>
  signInWithSso: (provider: 'azure' | 'saml', email: string) => Promise<void>
  signOut: () => void
}

/**
 * Manages add-in auth state. On mount it accepts a stored (non-expired) token, or
 * a token from Office SSO / env (`resolveAuthToken`). Otherwise the UI shows the
 * login screen. Auto-signs-out when the token expires.
 */
export function useAuth(): UseAuth {
  const [auth, setAuth] = useState<AuthState | null>(null)
  const [status, setStatus] = useState<UseAuth['status']>('checking')
  const [error, setError] = useState<string | null>(null)
  const [signingIn, setSigningIn] = useState(false)
  const expiryTimer = useRef<ReturnType<typeof setTimeout>>()

  const applyAuth = useCallback((next: AuthState | null) => {
    setAuth(next)
    setStatus(next ? 'authenticated' : 'unauthenticated')
  }, [])

  // Initial resolution: stored token first, then SSO/env fallback.
  useEffect(() => {
    let cancelled = false
    const stored = getStoredAuth()
    if (stored) {
      applyAuth(stored)
      return
    }
    resolveAuthToken().then((token) => {
      if (cancelled) return
      applyAuth(token ? { token } : null)
    })
    return () => {
      cancelled = true
    }
  }, [applyAuth])

  // Schedule auto sign-out at token expiry.
  useEffect(() => {
    if (expiryTimer.current) clearTimeout(expiryTimer.current)
    if (!auth?.expiresAt) return
    const ms = auth.expiresAt - Date.now()
    if (ms <= 0) {
      doLogout()
      applyAuth(null)
      return
    }
    expiryTimer.current = setTimeout(() => {
      doLogout()
      applyAuth(null)
    }, ms)
    return () => {
      if (expiryTimer.current) clearTimeout(expiryTimer.current)
    }
  }, [auth?.expiresAt, applyAuth])

  const signIn = useCallback(
    async (email: string, password: string) => {
      setSigningIn(true)
      setError(null)
      try {
        const next = await doLogin(email, password)
        applyAuth(next)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Login failed.')
        throw e
      } finally {
        setSigningIn(false)
      }
    },
    [applyAuth]
  )

  const signInWithSso = useCallback(
    async (provider: 'azure' | 'saml', email: string) => {
      setSigningIn(true)
      setError(null)
      try {
        const next = await startSso(provider, email)
        applyAuth(next)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'SSO sign-in failed.')
        throw e
      } finally {
        setSigningIn(false)
      }
    },
    [applyAuth]
  )

  const signOut = useCallback(() => {
    doLogout()
    applyAuth(null)
  }, [applyAuth])

  return { auth, status, error, signingIn, signIn, signInWithSso, signOut }
}
